/**
 * /fair-launch — Fair Launch discovery page.
 *
 * Pump.fun-style discovery interface using the SYNAPZ dark-cyan/violet identity.
 * All data sourced from genuine on-chain events via the indexer.
 *
 * Sections:
 *  1. Hero — tagline + CTA
 *  2. Discovery — search, filter, sort, market cards
 *  3. How it works — step cards
 *  4. Parameters — actual deployed testnet values
 */

import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button, Card, Pill } from "../components/ui";
import { ACTIVE_CHAIN } from "../config/chains";
import { formatEth } from "../lib/format";

// ── Types ──────────────────────────────────────────────────────────────────────

interface FairLaunchMarket {
  id: string;
  chainId: number;
  tokenAddress: string;
  marketAddress: string;
  creatorAddress: string;
  name: string;
  symbol: string;
  realEthReserve: string;
  tokenReserve: string;
  virtualEthReserve?: string | null;
  graduated: boolean;
  paused: boolean;
  createdAt: string;
  updatedAt: string;
  blockNumber: number;
  txHash: string;
  graduationThreshold?: string | null;
  graduationValuationUsd?: string | null;
  // oracleRoundId == "0" means market was created via emergency fallback oracle
  oracleRoundId?: string | null;
  // Volume stats (from indexer join)
  tradeCount?: number;
  totalVolumeEth?: string;
}

// ── Constants (TESTNET — scaled ÷100 from mainnet) ─────────────────────────────
//
// graduationValuationUsd = $500 (mainnet: $50,000)
// virtualReserveUsd = $30   (mainnet: ~$3,000)
// At MockV3Aggregator $2,000/ETH → virtualEthReserve ≈ 0.015 ETH, gradThresh ≈ 0.040 ETH

const TESTNET_GRAD_USD = 500;
const TESTNET_VIRTUAL_ETH = 0.015; // ETH

const DEPLOYED_PARAMS = [
  { label: "Total supply",           value: "1,000,000,000 tokens (fixed)" },
  { label: "Curve allocation",       value: "80% (800 M tokens)" },
  { label: "Migration allocation",   value: "20% (200 M tokens → DEX)" },
  { label: "Graduation target",      value: `$${TESTNET_GRAD_USD.toLocaleString()} USD (testnet; mainnet: $50,000)` },
  { label: "Virtual reserve",        value: `~${TESTNET_VIRTUAL_ETH} ETH (MockV3Aggregator $2,000/ETH)` },
  { label: "Oracle",                 value: "MockV3Aggregator — mainnet: Chainlink ETH/USD" },
  { label: "Oracle staleness",       value: "7,200 s (2 h)" },
  { label: "LP lock",                value: "30 days post-graduation" },
  { label: "Buy fee",                value: "1%" },
  { label: "Sell fee",               value: "1%" },
  { label: "Creation fee",           value: "0.0001 ETH" },
  { label: "Factory",                value: ACTIVE_CHAIN.fairLaunchFactoryAddress ?? "—" },
];

// ── Helpers ────────────────────────────────────────────────────────────────────

function shortAddr(a: string) { return `${a.slice(0, 6)}…${a.slice(-4)}`; }

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s  = Math.floor(ms / 1000);
  if (s < 60)   return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)   return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)   return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/** Curve-implied valuation in ETH: spotPrice × 1B tokens */
function impliedValuationEth(m: FairLaunchMarket): number | null {
  try {
    const V = m.virtualEthReserve ? Number(BigInt(m.virtualEthReserve)) / 1e18 : TESTNET_VIRTUAL_ETH;
    const R = Number(BigInt(m.realEthReserve)) / 1e18;
    const T = Number(BigInt(m.tokenReserve));
    if (T <= 0) return null;
    const spotPrice = (V + R) / T; // ETH per token (raw)
    return spotPrice * 1e9;         // ETH × 1B tokens
  } catch {
    return null;
  }
}

function gradProgress(m: FairLaunchMarket): number | null {
  if (!m.graduationThreshold) return null;
  try {
    const thresh = BigInt(m.graduationThreshold);
    if (thresh === 0n) return null;
    const real  = BigInt(m.realEthReserve);
    if (real >= thresh) return 100;
    return Number((real * 10000n) / thresh) / 100;
  } catch { return null; }
}

// ── Bonding curve mini-SVG (testnet-scaled) ────────────────────────────────────

function CurveMini({ progress }: { progress: number | null }) {
  const W = 60, H = 36, PAD = 4;
  const V = TESTNET_VIRTUAL_ETH;
  const GRAD = 0.040;
  const C = 800;

  const pts: string[] = [];
  for (let i = 0; i <= 40; i++) {
    const R = (i / 40) * GRAD;
    const T = (V * C) / (V + R);
    const price = (V + R) / T;
    const maxP = (V + GRAD) / ((V * C) / (V + GRAD));
    const x = PAD + (R / GRAD) * (W - PAD * 2);
    const y = H - PAD - Math.min(price / maxP, 1) * (H - PAD * 2);
    pts.push(`${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`);
  }
  const px = progress !== null
    ? PAD + (Math.min(progress, 100) / 100) * (W - PAD * 2) : null;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" aria-hidden="true">
      <path d={pts.join(" ")} fill="none" stroke="rgba(0,229,199,0.5)" strokeWidth="1.5" />
      {px !== null && (
        <line x1={px} y1={PAD} x2={px} y2={H - PAD}
          stroke="#12a3ff" strokeWidth="1" strokeDasharray="2 2" />
      )}
    </svg>
  );
}

// ── Market cards ────────────────────────────────────────────────────────────────

/** True when the market was created via the emergency fallback oracle (roundId == 0). */
function isFallbackOracle(m: FairLaunchMarket): boolean {
  return m.oracleRoundId === "0";
}

function MarketCard({ m }: { m: FairLaunchMarket }) {
  const prog     = gradProgress(m);
  const val      = impliedValuationEth(m);
  const age      = relativeTime(m.createdAt);
  const realEth  = BigInt(m.realEthReserve || "0");
  const gradVal  = m.graduationValuationUsd
    ? `${parseInt(m.graduationValuationUsd).toLocaleString()}`
    : `${TESTNET_GRAD_USD}`;

  return (
    <Link
      to={`/fair-launch/market/${ACTIVE_CHAIN.chainId}/${m.tokenAddress}`}
      className="block rounded-xl border border-line bg-panel/50 hover:border-plasma/40 hover:bg-panel/80 transition-all duration-150 p-4 space-y-3 group"
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-display font-semibold text-ink group-hover:text-plasma transition-colors truncate">
              {m.name}
            </span>
            <span className="font-mono text-[10px] text-muted bg-panel2/60 px-1.5 py-0.5 rounded">{m.symbol}</span>
            {m.graduated && <Pill tone="plasma">Graduated</Pill>}
            {m.paused && <Pill tone="warn">Paused</Pill>}
            {isFallbackOracle(m) && (
              <span
                className="inline-block rounded-full border border-warn/50 bg-warn/10 text-warn px-2 py-0.5 text-[10px] font-medium"
                title="Graduation threshold set from emergency fallback price — Chainlink was unavailable at creation. The threshold is immutable and may differ from an oracle-backed market."
              >
                ⚠ Emergency price
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] font-mono text-muted/60">{shortAddr(m.tokenAddress)}</span>
            <span className="text-[10px] text-muted/40">·</span>
            <span className="text-[10px] text-muted/50">{age}</span>
          </div>
        </div>
        <div className="text-right shrink-0 space-y-0.5">
          <p className="text-xs font-mono font-semibold text-ink">{formatEth(realEth)}</p>
          <p className="text-[10px] text-muted/60">raised</p>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2 text-[10px]">
        <div className="text-center">
          <p className="font-mono text-plasma">{val !== null ? `${val.toFixed(4)} Ξ` : "—"}</p>
          <p className="text-muted/60">valuation</p>
        </div>
        <div className="text-center">
          <p className="font-mono text-ink">{gradVal}</p>
          <p className="text-muted/60">grad target</p>
        </div>
        <div className="text-center">
          <p className="font-mono text-ink">{m.tradeCount ?? 0}</p>
          <p className="text-muted/60">trades</p>
        </div>
      </div>

      {/* Curve preview + progress */}
      <div className="flex items-end gap-3">
        <div className="w-16 h-9 shrink-0 opacity-70">
          <CurveMini progress={prog} />
        </div>
        <div className="flex-1 space-y-1">
          <div className="flex justify-between text-[10px] text-muted/50">
            <span>Graduation progress</span>
            <span>{prog !== null ? `${prog.toFixed(1)}%` : "—"}</span>
          </div>
          <div className="h-1 bg-line/40 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-plasma/60 to-plasma rounded-full transition-all"
              style={{ width: `${Math.min(prog ?? 0, 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-[10px] text-muted/40 pt-1 border-t border-line/20">
        <span>Creator: {shortAddr(m.creatorAddress)}</span>
        <span className="text-muted/30">RH Testnet</span>
      </div>
    </Link>
  );
}

function MarketListRow({ m }: { m: FairLaunchMarket }) {
  const prog    = gradProgress(m);
  const val     = impliedValuationEth(m);
  const age     = relativeTime(m.createdAt);
  const realEth = BigInt(m.realEthReserve || "0");

  return (
    <Link
      to={`/fair-launch/market/${ACTIVE_CHAIN.chainId}/${m.tokenAddress}`}
      className="flex items-center gap-3 px-4 py-3 rounded-lg border border-line/50 bg-panel/40 hover:border-plasma/30 hover:bg-panel/70 transition-all duration-150 group"
    >
      {/* Name */}
      <div className="min-w-0 w-36 shrink-0">
        <div className="flex items-center gap-1 flex-wrap">
          <p className="font-semibold text-sm text-ink group-hover:text-plasma transition-colors truncate">{m.name}</p>
          {isFallbackOracle(m) && (
            <span
              className="inline-block shrink-0 rounded-full border border-warn/50 bg-warn/10 text-warn px-1.5 py-px text-[9px] font-medium"
              title="Graduation threshold set from emergency fallback price — Chainlink was unavailable at creation. The threshold is immutable and may differ from an oracle-backed market."
            >
              ⚠
            </span>
          )}
        </div>
        <p className="font-mono text-[10px] text-muted/60">{m.symbol}</p>
      </div>
      {/* Curve mini */}
      <div className="w-14 h-8 shrink-0 opacity-60">
        <CurveMini progress={prog} />
      </div>
      {/* Progress */}
      <div className="flex-1 min-w-0">
        <div className="h-1.5 bg-line/40 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-plasma/50 to-plasma rounded-full"
            style={{ width: `${Math.min(prog ?? 0, 100)}%` }}
          />
        </div>
        <p className="text-[10px] text-muted/50 mt-0.5">{prog !== null ? `${prog.toFixed(1)}% of graduation` : "No data"}</p>
      </div>
      {/* ETH raised */}
      <div className="w-20 text-right shrink-0">
        <p className="font-mono text-sm text-ink">{formatEth(realEth)}</p>
        <p className="text-[10px] text-muted/50">raised</p>
      </div>
      {/* Valuation */}
      <div className="w-24 text-right shrink-0 hidden sm:block">
        <p className="font-mono text-xs text-plasma">{val !== null ? `${val.toFixed(3)} Ξ` : "—"}</p>
        <p className="text-[10px] text-muted/50">valuation</p>
      </div>
      {/* Trades */}
      <div className="w-14 text-right shrink-0 hidden md:block">
        <p className="font-mono text-xs text-ink">{m.tradeCount ?? 0}</p>
        <p className="text-[10px] text-muted/50">trades</p>
      </div>
      {/* Age + status */}
      <div className="w-20 text-right shrink-0 hidden lg:block">
        <p className="text-xs text-muted/70">{age}</p>
        {m.graduated && <Pill tone="plasma">Grad</Pill>}
      </div>
    </Link>
  );
}

// ── Discovery ──────────────────────────────────────────────────────────────────

type FilterTab = "all" | "active" | "near_graduation" | "graduated";
type SortKey   = "newest" | "near_graduation" | "most_traded";
type ViewMode  = "grid" | "list";

const FILTER_TABS: { key: FilterTab; label: string }[] = [
  { key: "all",             label: "All"            },
  { key: "active",          label: "Active"         },
  { key: "near_graduation", label: "Near Grad"      },
  { key: "graduated",       label: "Graduated"      },
];

function applyClientFilter(markets: FairLaunchMarket[], tab: FilterTab, search: string): FairLaunchMarket[] {
  let list = markets;

  if (search) {
    const q = search.toLowerCase();
    list = list.filter(m =>
      m.name.toLowerCase().includes(q) ||
      m.symbol.toLowerCase().includes(q) ||
      m.tokenAddress.startsWith(q) ||
      m.creatorAddress.startsWith(q),
    );
  }

  switch (tab) {
    case "active":
      list = list.filter(m => !m.graduated && !m.paused);
      break;
    case "near_graduation":
      list = list.filter(m => {
        const p = gradProgress(m);
        return !m.graduated && p !== null && p >= 50;
      });
      break;
    case "graduated":
      list = list.filter(m => m.graduated);
      break;
  }

  return list;
}

function applyClientSort(markets: FairLaunchMarket[], sort: SortKey): FairLaunchMarket[] {
  return [...markets].sort((a, b) => {
    switch (sort) {
      case "most_traded":
        return (b.tradeCount ?? 0) - (a.tradeCount ?? 0);
      case "near_graduation": {
        const pa = gradProgress(a) ?? 0;
        const pb = gradProgress(b) ?? 0;
        return pb - pa;
      }
      case "newest":
      default:
        return b.blockNumber - a.blockNumber;
    }
  });
}

function DiscoverySection() {
  const [markets, setMarkets]   = useState<FairLaunchMarket[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const [search, setSearch]     = useState("");
  const [tab, setTab]           = useState<FilterTab>("all");
  const [sort, setSort]         = useState<SortKey>("newest");
  const [view, setView]         = useState<ViewMode>("grid");

  const fetchMarkets = () => {
    setLoading(true);
    setError(null);
    fetch(`/api/fair-launch/markets?chainId=${ACTIVE_CHAIN.chainId}&limit=100`)
      .then(r => {
        if (!r.ok) throw new Error(`API error ${r.status}`);
        return r.json() as Promise<{ markets: FairLaunchMarket[] }>;
      })
      .then(d => {
        setMarkets(d.markets);
        setLastFetched(new Date());
      })
      .catch(e => {
        setError((e as Error).message);
        setMarkets([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchMarkets();
    const interval = setInterval(fetchMarkets, 30_000); // poll every 30 s
    return () => clearInterval(interval);
  }, []);

  const displayed = useMemo(
    () => applyClientSort(applyClientFilter(markets, tab, search), sort),
    [markets, tab, search, sort],
  );

  return (
    <section className="space-y-4">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search */}
        <div className="relative flex-1">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted/50"
            viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
          </svg>
          <input
            type="text"
            placeholder="Search name, symbol, address…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm bg-panel2/50 border border-line/50 rounded-lg text-ink placeholder:text-muted/40 focus:outline-none focus:border-plasma/50 transition-colors"
          />
          {search && (
            <button onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted/40 hover:text-muted transition-colors text-xs">
              ✕
            </button>
          )}
        </div>

        {/* Sort */}
        <select
          value={sort}
          onChange={e => setSort(e.target.value as SortKey)}
          className="text-sm bg-panel2/50 border border-line/50 rounded-lg px-3 py-2 text-ink focus:outline-none focus:border-plasma/50 cursor-pointer"
        >
          <option value="newest">Newest first</option>
          <option value="near_graduation">Near graduation</option>
          <option value="most_traded">Most traded</option>
        </select>

        {/* View toggle */}
        <div className="flex border border-line/50 rounded-lg overflow-hidden shrink-0">
          {(["grid", "list"] as ViewMode[]).map(v => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-3 py-2 text-xs font-medium transition-colors ${
                view === v ? "bg-plasma/15 text-plasma" : "text-muted hover:text-ink hover:bg-panel2/30"
              }`}
            >
              {v === "grid" ? (
                <svg viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5" aria-label="Grid view">
                  <rect x="1" y="1" width="6" height="6" rx="1" />
                  <rect x="9" y="1" width="6" height="6" rx="1" />
                  <rect x="1" y="9" width="6" height="6" rx="1" />
                  <rect x="9" y="9" width="6" height="6" rx="1" />
                </svg>
              ) : (
                <svg viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5" aria-label="List view">
                  <rect x="1" y="2" width="14" height="2" rx="1" />
                  <rect x="1" y="7" width="14" height="2" rx="1" />
                  <rect x="1" y="12" width="14" height="2" rx="1" />
                </svg>
              )}
            </button>
          ))}
        </div>

        {/* Refresh */}
        <button
          onClick={fetchMarkets}
          className="text-xs text-muted/50 hover:text-plasma transition-colors px-2"
          title={lastFetched ? `Last updated ${relativeTime(lastFetched.toISOString())}` : "Refresh"}
        >
          ↻
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 border-b border-line/30 pb-0">
        {FILTER_TABS.map(({ key, label }) => {
          const count = key === "all" ? markets.length
            : applyClientFilter(markets, key, "").length;
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-t-md transition-colors -mb-px border-b-2 ${
                tab === key
                  ? "border-plasma text-plasma bg-plasma/5"
                  : "border-transparent text-muted hover:text-ink"
              }`}
            >
              {label}
              {count > 0 && (
                <span className={`ml-1.5 text-[10px] px-1 rounded-full ${
                  tab === key ? "bg-plasma/20 text-plasma" : "bg-line/40 text-muted/60"
                }`}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Market list / grid */}
      {loading ? (
        <div className={view === "grid"
          ? "grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          : "space-y-2"}>
          {[...Array(6)].map((_, i) => (
            <div key={i} className={`rounded-xl border border-line/40 bg-panel/30 animate-pulse ${
              view === "grid" ? "h-52" : "h-16"
            }`} />
          ))}
        </div>
      ) : error ? (
        <Card className="text-center py-10 space-y-3">
          <p className="text-sm font-semibold text-danger">Could not load markets</p>
          <p className="text-xs text-muted/60">{error}</p>
          <p className="text-xs text-muted/60">
            The indexer may still be syncing blocks since factory deployment. Check back shortly.
          </p>
          <Button variant="ghost" onClick={fetchMarkets}>Retry</Button>
        </Card>
      ) : displayed.length === 0 ? (
        <Card className="text-center py-12 space-y-3">
          {markets.length === 0 ? (
            <>
              <div className="w-10 h-10 rounded-full bg-line/40 flex items-center justify-center mx-auto">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
                  className="w-5 h-5 text-muted/60">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 8v4M12 16h.01" strokeLinecap="round" />
                </svg>
              </div>
              <p className="text-muted text-sm font-medium">No markets indexed yet</p>
              <p className="text-xs text-muted/60 max-w-xs mx-auto">
                Markets appear here once someone creates a fair launch and the indexer
                processes the on-chain event. The indexer polls every ~12 seconds.
              </p>
              <Link to="/fair-launch/create">
                <Button className="mt-1">Create the first market →</Button>
              </Link>
            </>
          ) : (
            <>
              <p className="text-muted text-sm">No markets match your search.</p>
              <button onClick={() => { setSearch(""); setTab("all"); }}
                className="text-xs text-plasma hover:underline">
                Clear filters
              </button>
            </>
          )}
        </Card>
      ) : view === "grid" ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {displayed.map(m => <MarketCard key={m.id} m={m} />)}
        </div>
      ) : (
        <div className="space-y-2">
          {/* List header */}
          <div className="hidden md:flex items-center gap-3 px-4 py-1 text-[10px] font-semibold text-muted/50 uppercase tracking-wide">
            <div className="w-36">Token</div>
            <div className="w-14" />
            <div className="flex-1">Progress</div>
            <div className="w-20 text-right">Raised</div>
            <div className="w-24 text-right hidden sm:block">Valuation</div>
            <div className="w-14 text-right hidden md:block">Trades</div>
            <div className="w-20 text-right hidden lg:block">Age</div>
          </div>
          {displayed.map(m => <MarketListRow key={m.id} m={m} />)}
        </div>
      )}

      {/* Count */}
      {!loading && displayed.length > 0 && (
        <p className="text-center text-xs text-muted/40">
          {displayed.length} market{displayed.length !== 1 ? "s" : ""}
          {search || tab !== "all" ? " (filtered)" : ""}
          {lastFetched && ` · updated ${relativeTime(lastFetched.toISOString())}`}
        </p>
      )}
    </section>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function FairLaunchPage() {
  return (
    <div className="space-y-10">
      {/* Hero */}
      <section className="rounded-2xl border border-line bg-panel/30 px-7 py-10 sm:px-12 sm:py-14 grid-etch relative overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-24 -right-16 w-96 h-96 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(0,229,199,0.07) 0%, transparent 70%)" }}
        />
        <div className="relative flex flex-col lg:flex-row lg:items-start lg:gap-16">
          <div className="flex-1 max-w-lg space-y-5">
            <div className="flex items-center gap-2 flex-wrap">
              <Pill tone="plasma">Live on Testnet</Pill>
              <Pill tone="warn">Testnet only</Pill>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight tracking-tight text-gradient">
              Fair Launch
              <br />
              Bonding Curve
            </h1>
            <p className="text-muted text-base sm:text-lg leading-relaxed">
              Launch a token with a built-in bonding curve. Price starts near zero and rises
              automatically as people buy. Graduates to a DEX pool when the implied
              market cap reaches{" "}
              <strong className="text-plasma">${TESTNET_GRAD_USD.toLocaleString()} USD</strong>{" "}
              (testnet ÷100 — mainnet target: $50,000). No pre-mine. No private sale. No insider allocation.
            </p>
            <div className="flex flex-wrap gap-3 pt-1">
              <Link to="/fair-launch/create">
                <Button className="px-6 py-2.5 text-base">
                  Create Fair Launch →
                </Button>
              </Link>
            </div>
            <p className="text-xs text-warn/80">
              Robinhood Chain Testnet · All ETH and tokens are test assets with no real value.
              Graduation uses a simulation adapter — no real DEX liquidity is created.
            </p>
          </div>

          {/* Curve illustration */}
          <div className="hidden lg:flex flex-col items-center gap-2 shrink-0 mt-4">
            <MiniHeroCurve />
            <p className="text-[10px] text-muted/50 font-mono text-center">
              price curve · testnet scale
            </p>
          </div>
        </div>
      </section>

      {/* Discovery */}
      <DiscoverySection />

      {/* How it works */}
      <section className="space-y-4">
        <h2 className="font-display text-xl font-bold">How a fair launch works</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            {
              n: "01",
              title: "Token deployed",
              body: "1 billion tokens minted. 80% placed on the bonding curve. 20% reserved for the DEX pool at graduation. Creator gets 0% — they buy just like everyone else.",
            },
            {
              n: "02",
              title: "Curve phase",
              body: "Price starts near zero and rises automatically on each buy. Sell at any time. The curve follows k = (V + R) × T where V is the virtual ETH reserve set by the oracle at launch.",
            },
            {
              n: "03",
              title: "Graduation",
              body: "When the curve-implied valuation reaches the USD target, trading stops. The ETH threshold is computed from the Chainlink oracle at launch and stored immutably — it never changes. All ETH + 200M tokens migrate to a DEX pool locked for 30 days.",
            },
          ].map(({ n, title, body }) => (
            <Card key={n} className="space-y-3">
              <div className="flex items-center gap-3">
                <span className="inline-flex w-8 h-8 items-center justify-center rounded-full border border-plasma/30 bg-plasma/10 font-mono text-xs font-bold text-plasma shrink-0">
                  {n}
                </span>
                <h3 className="font-display font-semibold text-ink">{title}</h3>
              </div>
              <p className="text-sm text-muted leading-relaxed">{body}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Deployed parameters */}
      <section className="space-y-4">
        <div className="flex items-center gap-3">
          <h2 className="font-display text-xl font-bold">Deployed parameters</h2>
          <Pill tone="warn">Testnet ÷100</Pill>
        </div>
        <Card className="overflow-hidden p-0">
          <table className="w-full text-sm">
            <tbody>
              {DEPLOYED_PARAMS.map(({ label, value }, i) => (
                <tr key={label} className={`${i % 2 === 0 ? "bg-panel2/30" : ""} border-b border-line/30 last:border-0`}>
                  <td className="px-5 py-2.5 text-muted">{label}</td>
                  <td className="px-5 py-2.5 font-mono text-plasma text-right text-xs break-all">{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-5 py-3 bg-warn/5 border-t border-warn/20">
            <p className="text-xs text-warn/80">
              Testnet parameters are scaled ÷100 from mainnet values. The graduation ETH threshold is
              computed per-market from the MockV3Aggregator oracle at creation time and stored immutably.
            </p>
          </div>
        </Card>
      </section>
    </div>
  );
}

// ── Hero curve SVG ─────────────────────────────────────────────────────────────

function MiniHeroCurve() {
  const W = 280, H = 140, PAD = 24;
  const V = TESTNET_VIRTUAL_ETH, GRAD = 0.040, C = 800;
  const innerW = W - PAD * 2, innerH = H - PAD * 2;

  const pts: string[] = [];
  const maxP = (V + GRAD) / ((V * C) / (V + GRAD));
  for (let i = 0; i <= 60; i++) {
    const R = (i / 60) * GRAD;
    const T = (V * C) / (V + R);
    const price = (V + R) / T;
    const x = PAD + (R / GRAD) * innerW;
    const y = H - PAD - Math.min(price / maxP, 1) * innerH;
    pts.push(`${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`);
  }
  const path = pts.join(" ");
  const gradX = PAD + innerW;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-64" role="img" aria-label="Bonding curve">
      <defs>
        <linearGradient id="heroGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#00e5c7" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#00e5c7" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke="rgba(0,229,199,0.15)" strokeWidth="1" />
      <line x1={PAD} y1={PAD}     x2={PAD}     y2={H - PAD} stroke="rgba(0,229,199,0.15)" strokeWidth="1" />
      <line x1={gradX} y1={PAD}   x2={gradX}   y2={H - PAD} stroke="rgba(0,229,199,0.3)" strokeWidth="1" strokeDasharray="4 4" />
      <text x={gradX + 4} y={PAD + 10} fill="rgba(0,229,199,0.55)" fontSize="8.5" fontFamily="monospace">~$500</text>
      <path
        d={`${path} L${gradX.toFixed(1)},${(H - PAD).toFixed(1)} L${PAD},${(H - PAD).toFixed(1)} Z`}
        fill="url(#heroGrad)"
      />
      <path d={path} fill="none" stroke="#00e5c7" strokeWidth="2" strokeLinecap="round" />
      <text x={W / 2} y={H - 5} fill="rgba(200,210,230,0.3)" fontSize="8" textAnchor="middle" fontFamily="monospace">ETH raised →</text>
    </svg>
  );
}
